import {
  AnyPgColumn,
  date,
  foreignKey,
  index,
  pgTable,
  uuid,
  pgEnum,
  varchar,
} from 'drizzle-orm/pg-core';
import { order_photos } from '@trashlab/api/src/domains/orders/schemas/order-photos';
import { dump_ticket } from '@trashlab/api/src/domains/orders/schemas/dump-ticket';
import { organization_site } from '@trashlab/api/src/domains/dispatch/schemas/organization-site';
import { gpt_one_shot_completion } from '@trashlab/api/src/domains/ai/schemas/gpt-one-shot-completion';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';
import { assets } from '@trashlab/api/src/domains/asset/schemas/assets';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { decimal } from '@trashlab/api/src/domains/core/schemas/base-template';
import { dump_unit } from '@trashlab/api/src/domains/orders/schemas/dump-ticket';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';

export const status = pgEnum('status', ['PENDING', 'COMPLETED']);

export const dump_ticket_extraction_run = pgTable(
  'dump_ticket_extraction_run',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    textractRunId: uuid('textractRunId'),
    gptOneShotCompletionId: uuid('gptOneShotCompletionId'),
    ticketNumber: varchar('ticketNumber', { length: 255 }),
    ticketDate: date('ticketDate'),
    dumpSiteId: uuid('dumpSiteId'),
    dumpSiteName: varchar('dumpSiteName', { length: 255 }),
    materialId: uuid('materialId'),
    materialName: varchar('materialName', { length: 255 }),
    rawMaterialName: varchar('rawMaterialName', { length: 255 }),
    tonsDumped: decimal('tonsDumped'),
    weightValue: decimal('weightValue'),
    weightUnit: dump_unit('weightUnit'),
    photoId: uuid('photoId')
      .notNull()
      .references((): AnyPgColumn => order_photos.id),
    status: status('status').default('PENDING'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    totalCost: decimal('totalCost'), // todo figure out if these should be currency
    totalCostPerUnit: decimal('totalCostPerUnit'), // todo figure out if these should be currency
    unit: varchar('unit', { length: 255 }),
    dumpTicketId: uuid('dumpTicketId'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('dump_ticket_extraction_run_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('dump_ticket_extraction_run_tenantId_idx').on(
        table.tenantId,
      ),
      updatedAtTs_idx: index('dump_ticket_extraction_run_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('dump_ticket_extraction_run_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index(
        'dump_ticket_extraction_run_versionNumber_idx',
      ).on(table.versionNumber),

      dumpSiteId_idx: index('dump_ticket_extraction_run_dumpSiteId_idx').on(
        table.dumpSiteId,
      ),
      dumpTicketId_idx: index('dump_ticket_extraction_run_dumpTicketId_idx').on(
        table.dumpTicketId,
      ),
      gptOneShotCompletionId_idx: index(
        'dump_ticket_extraction_run_gptOneShotCompletionId_idx',
      ).on(table.gptOneShotCompletionId),
      materialId_idx: index('dump_ticket_extraction_run_materialId_idx').on(
        table.materialId,
      ),
      photoId_idx: index('dump_ticket_extraction_run_photoId_idx').on(
        table.photoId,
      ),
      //foreign keys
      dump_ticket_extraction_run_dumpSiteId_fkey: foreignKey({
        columns: [table.dumpSiteId],
        foreignColumns: [organization_site.id],
        name: 'dump_ticket_extraction_run_dumpSiteId_fkey',
      }),
      dump_ticket_extraction_run_dumpTicketId_fkey: foreignKey({
        columns: [table.dumpTicketId],
        foreignColumns: [dump_ticket.id],
        name: 'dump_ticket_extraction_run_dumpTicketId_fkey',
      }),
      dump_ticket_extraction_run_gptOneShotCompletionId_fkey: foreignKey({
        columns: [table.gptOneShotCompletionId],
        foreignColumns: [gpt_one_shot_completion.id],
        name: 'dump_ticket_extraction_run_gptOneShotCompletionId_fkey',
      }),
      dump_ticket_extraction_run_materialId_fkey: foreignKey({
        columns: [table.materialId],
        foreignColumns: [material.id],
        name: 'dump_ticket_extraction_run_materialId_fkey',
      }),
      //dump_ticket_extraction_run_photoId_order_photos_id_fk
      // dump_ticket_extraction_run_photoId_fkey: foreignKey({
      //   columns: [table.photoId],
      //   foreignColumns: [order_photos.id as AnyPgColumn],
      //   name: 'dump_ticket_extraction_run_photoId_fkey',
      // }),
    };
  },
);

export const dump_ticket_extraction_run_history = pgTable(
  'dump_ticket_extraction_run_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    textractRunId: uuid('textractRunId'),
    gptOneShotCompletionId: uuid('gptOneShotCompletionId'),
    ticketNumber: varchar('ticketNumber', { length: 255 }),
    ticketDate: date('ticketDate'),
    dumpSiteId: uuid('dumpSiteId'),
    dumpSiteName: varchar('dumpSiteName', { length: 255 }),
    materialId: uuid('materialId'),
    materialName: varchar('materialName', { length: 255 }),
    rawMaterialName: varchar('rawMaterialName', { length: 255 }),
    tonsDumped: decimal('tonsDumped'),
    weightValue: decimal('weightValue'),
    weightUnit: dump_unit('weightUnit'),
    photoId: uuid('photoId'),
    status: status('status'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    totalCost: decimal('totalCost'),
    totalCostPerUnit: decimal('totalCostPerUnit'),
    unit: varchar('unit', { length: 255 }),
    dumpTicketId: uuid('dumpTicketId'),
  },
);

export const asset_extraction_run = pgTable(
  'asset_extraction_run',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    photoId: uuid('photoId').notNull(),
    gptOneShotCompletionId: uuid('gptOneShotCompletionId'),
    productId: uuid('productId'),
    assetId: uuid('assetId'),
    status: status('status').default('PENDING'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('asset_extraction_run_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('asset_extraction_run_tenantId_idx').on(
        table.tenantId,
      ),
      updatedAtTs_idx: index('asset_extraction_run_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('asset_extraction_run_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('asset_extraction_run_versionNumber_idx').on(
        table.versionNumber,
      ),

      assetId_idx: index('asset_extraction_run_assetId_idx').on(table.assetId),
      gptOneShotCompletionId_idx: index(
        'asset_extraction_run_gptOneShotCompletionId_idx',
      ).on(table.gptOneShotCompletionId),
      photoId_idx: index('asset_extraction_run_photoId_idx').on(table.photoId),
      productId_idx: index('asset_extraction_run_productId_idx').on(
        table.productId,
      ),

      //foreign keys
      asset_extraction_run_assetId_fkey: foreignKey({
        columns: [table.assetId],
        foreignColumns: [assets.id],
        name: 'asset_extraction_run_assetId_fkey',
      }),
      asset_extraction_run_gptOneShotCompletionId_fkey: foreignKey({
        columns: [table.gptOneShotCompletionId],
        foreignColumns: [gpt_one_shot_completion.id],
        name: 'asset_extraction_run_gptOneShotCompletionId_fkey',
      }),
      asset_extraction_run_photoId_fkey: foreignKey({
        columns: [table.photoId],
        foreignColumns: [order_photos.id],
        name: 'asset_extraction_run_photoId_fkey',
      }),
      asset_extraction_run_productId_fkey: foreignKey({
        columns: [table.productId],
        foreignColumns: [product.id],
        name: 'asset_extraction_run_productId_fkey',
      }),
    };
  },
);

export const asset_extraction_run_history = pgTable(
  'asset_extraction_run_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    photoId: uuid('photoId'),
    gptOneShotCompletionId: uuid('gptOneShotCompletionId'),
    productId: uuid('productId'),
    assetId: uuid('assetId'),
    status: status('status'),
  },
);
